import { omit } from "lodash";
import {
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";

/**
 * CLE-2996 — the "No deductible" choice on Remaining Deductible (primary insurance).
 *
 * Since CLE-2753 a blank deductible means "the payer did not say" and a 0 means "the answer is zero", so the form never
 * fills a 0 in on its own. This choice is how Ani's rule (below) says "the answer is zero" for a plan that has no
 * deductible at all. It has no control of its own (Ani, 2026-10-20: no checkbox, just the placeholder text):
 *  - ON: the deductible holds a real 0, the box is left empty with the placeholder "No deductible", and the saved
 *    estimate records `benefitDetails.noDeductible: true` so a "no deductible" plan can be told from a typed 0 later.
 *  - OFF: the record is gone. The box stays editable while ON: typing a number in it (or picking a figure in the details
 *    panel) withdraws the choice and the typed number is the deductible; deleting it back to empty leaves it BLANK.
 *
 * It is set ONLY by the payer's response — on when the eligibility response says `deductibleNotApplicable` (the API's
 * reading of Ani's rule, 2026-10-19: pVerify reports the calendar-year AND the remaining deductible as N/A); each new
 * response re-applies the rule, so a response without the flag switches an earlier ON back off — or restored from a
 * saved estimate on draft / copy / redo (`readNoDeductibleChoice`).
 *
 * The record lives in `Order.benefitDetails`, which is either a whole `{ request, response }` eligibility pair or null,
 * so the choice can be on only when an eligibility response is on the form (`hasEligibilityResponse`).
 */

export const NO_DEDUCTIBLE_FIELD = "noDeductible";

interface IFormAccess {
  setValue: UseFormSetValue<FieldValues>;
  getValues: UseFormGetValues<FieldValues>;
}

export const setNoDeductibleChoice = (
  { setValue, getValues }: IFormAccess,
  on: boolean
): void => {
  if (on) {
    setValue(NO_DEDUCTIBLE_FIELD, true);
    setValue("deductible", 0);
    return;
  }
  // Only a choice that was on owns the 0 in the box; an estimator's own typed number is never cleared.
  if (getValues(NO_DEDUCTIBLE_FIELD) === true) {
    setValue(NO_DEDUCTIBLE_FIELD, false);
    setValue("deductible", null);
  }
};

/** Re-applies Ani's rule from a new eligibility response (primary side only). */
export const applyEligibilityNoDeductible = (
  form: IFormAccess,
  response: { deductibleNotApplicable?: boolean } | null | undefined
): void =>
  setNoDeductibleChoice(form, response?.deductibleNotApplicable === true);

/** Whether the form holds an eligibility response the choice can be recorded on. */
export const hasEligibilityResponse = (benefitDetails: any): boolean =>
  !!benefitDetails?.response && typeof benefitDetails.response === "object";

/** The choice as a saved estimate recorded it — for draft / copy / redo. */
export const readNoDeductibleChoice = (benefitDetails: any): boolean =>
  hasEligibilityResponse(benefitDetails) &&
  benefitDetails.noDeductible === true;

/**
 * The `benefitDetails` to save: the record is added while the choice is on, and removed otherwise (a draft reopened
 * with the choice on, then switched off, must not keep it).
 */
export const withNoDeductibleRecord = (
  benefitDetails: any,
  noDeductible: unknown
): any => {
  if (!hasEligibilityResponse(benefitDetails)) return benefitDetails;
  const rest = omit(benefitDetails, NO_DEDUCTIBLE_FIELD);
  return noDeductible === true ? { ...rest, noDeductible: true } : rest;
};
